import { useMemo, useCallback } from 'react';
import { getColors } from '../../components/utils/getColors';
import {
  CalculationStrategies,
  ProjectSample
} from '../../@types/samples/types';
import { Typography } from '../typography';
import CollapsiblePanel from './CollapsiblePanel';

export interface CorrelationOptionsProps {
  isDay: boolean;
  loadingProjectSamples: boolean;
  editCorrelationSamples: boolean;
  projectSamples: ProjectSample[];
  handleSampleToggle: (uid: string, strategy: CalculationStrategies) => void;
  handleSelectAllStrategySamples: (strategy: CalculationStrategies, checked: boolean) => void;
  /**
   * Whether a sample should be listed under a given strategy. Defaults to the
   * `performed_analyses` flag; callers can inject stricter policy (e.g. only
   * samples with a real computed result) without coupling the design system
   * to app-level domain helpers.
   */
  isSampleAvailableForStrategy?: (
    sample: ProjectSample,
    strategy: CalculationStrategies
  ) => boolean;
}

const defaultIsSampleAvailableForStrategy = (
  sample: ProjectSample,
  strategy: CalculationStrategies
): boolean => sample.performed_analyses.includes(strategy);

type TStrategySamples = {
  samples: ProjectSample[];
  totalSelected: number;
};

const strategyColors = {
  capillaryPressure: '#9c27b0',
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: '#2196f3',
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: '#f44336'
};

const strategyLabels = {
  capillaryPressure: 'CAPILLARY PRESSURE',
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: 'WATER-OIL',
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: 'GAS-OIL'
};

const strategiesArray = [
  'capillaryPressure',
  CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY,
  CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
] as const;

const subStrategyLabels = {
  [CalculationStrategies.LIAO]: 'LIAO',
  [CalculationStrategies.LAMBDA]: 'BROOKS-COREY'
};

const subStrategyColors = {
  [CalculationStrategies.LIAO]: '#7e57c2',
  [CalculationStrategies.LAMBDA]: '#26a69a'
};

const Tag = ({ color, children }: { color: string; children: React.ReactNode }) => (
  <span style={{
    backgroundColor: color,
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.5px'
  }}>
    {children}
  </span>
);

const CorrelationOptions = ({
  isDay,
  loadingProjectSamples,
  editCorrelationSamples,
  projectSamples,
  handleSampleToggle,
  handleSelectAllStrategySamples,
  isSampleAvailableForStrategy = defaultIsSampleAvailableForStrategy
}: CorrelationOptionsProps) => {
  const colors = getColors(isDay);

  const strategySamples = useMemo(() => {
    const groupByStrategy = (strategy: CalculationStrategies) => {
      const samples = projectSamples.filter(s =>
        isSampleAvailableForStrategy(s, strategy)
      );
      const totalSelected = samples.filter(
        s => s.selectedAnalyses[strategy]
      ).length;
      return { samples, totalSelected };
    };

    return {
      capillaryPressure: {
        [CalculationStrategies.LIAO]: groupByStrategy(CalculationStrategies.LIAO),
        [CalculationStrategies.LAMBDA]: groupByStrategy(CalculationStrategies.LAMBDA)
      },
      [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: groupByStrategy(CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY),
      [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: groupByStrategy(CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY)
    };
  }, [projectSamples, isSampleAvailableForStrategy]);

  const getStrategySelectAllStatus = useCallback((strategyGroup: TStrategySamples) => {
    const samples = strategyGroup?.samples || [];
    const totalSelected = strategyGroup?.totalSelected || 0;

    return {
      checked: samples.length > 0 && totalSelected === samples.length,
      indeterminate: totalSelected > 0 && totalSelected < samples.length
    };
  }, []);

  const isSampleChecked = useCallback((sample: ProjectSample, strategy: CalculationStrategies) => {
    return sample.selectedAnalyses?.[strategy] ?? false;
  }, []);

  const CustomCheckbox = ({ checked, indeterminate, onChange, label, disabled }: any) => {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
        <input
          type="checkbox"
          checked={checked}
          ref={input => {
            if (input) {
              input.indeterminate = indeterminate;
            }
          }}
          onChange={onChange}
          disabled={disabled}
        />
        {label && <Typography.Text size="sm">{label}</Typography.Text>}
      </label>
    );
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {!loadingProjectSamples ? (
        strategiesArray.map(strategy => {
          if (strategy === 'capillaryPressure') {
            const capillarySamples = strategySamples.capillaryPressure;
            if (!capillarySamples) return null;
            return (
              <CollapsiblePanel
                key="capillaryPressure"
                isDay={isDay}
                header={
                  <div style={{ display: 'flex', alignItems: 'center'}}>
                    <Tag color={strategyColors[strategy]}>{strategyLabels[strategy]}</Tag>
                  </div>
                }
              >
                <div style={{ display: 'flex', flexDirection: 'column'}}>
                  {[CalculationStrategies.LIAO, CalculationStrategies.LAMBDA].map(subStrategy => {
                    const samplesGroup: TStrategySamples = capillarySamples[subStrategy];
                    const { checked, indeterminate } = getStrategySelectAllStatus(samplesGroup);
                    return (
                      <CollapsiblePanel
                        key={subStrategy}
                        isDay={isDay}
                        defaultOpen={true}
                        header={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                            <CustomCheckbox
                              checked={checked}
                              indeterminate={indeterminate}
                              onChange={(event: any) => handleSelectAllStrategySamples(subStrategy, event.target.checked)}
                            />
                            <Tag color={subStrategyColors[subStrategy]}>{subStrategyLabels[subStrategy]}</Tag>
                            <Typography.Text size="xs" color="muted">
                              ({capillarySamples[subStrategy]?.totalSelected || 0}/{capillarySamples[subStrategy]?.samples.length || 0})
                            </Typography.Text>
                          </div>
                        }
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                          {samplesGroup?.samples.map(sample => (
                            <CustomCheckbox
                              key={sample.uid}
                              checked={isSampleChecked(sample, subStrategy)}
                              onChange={() => handleSampleToggle(sample.uid, subStrategy)}
                              label={sample.sample_code}
                            />
                          ))}
                        </div>
                      </CollapsiblePanel>
                    );
                  })}
                </div>
              </CollapsiblePanel>
            );
          }

          const { checked, indeterminate } = getStrategySelectAllStatus(strategySamples[strategy]);

          return (
            <CollapsiblePanel
              key={strategy}
              isDay={isDay}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <CustomCheckbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={(event: any) => handleSelectAllStrategySamples(strategy, event.target.checked)}
                  />
                  <Tag color={strategyColors[strategy]}>{strategyLabels[strategy]}</Tag>
                  <Typography.Text size="xs" color="muted">
                    ({strategySamples[strategy]?.totalSelected || 0}/{strategySamples[strategy]?.samples.length || 0})
                  </Typography.Text>
                </div>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {strategySamples[strategy]?.samples.map(sample => (
                  <CustomCheckbox
                    key={sample.uid}
                    checked={isSampleChecked(sample, strategy)}
                    onChange={() => handleSampleToggle(sample.uid, strategy)}
                    label={sample.sample_code}
                  />
                ))}
              </div>
            </CollapsiblePanel>
          );
        })
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '1.5rem', alignItems: 'center' }}>
          <Typography.Text color="muted">Loading...</Typography.Text>
        </div>
      )}
    </div>
  );
};

export default CorrelationOptions;
